import type { BadgeVariant } from "../../components/ui";
import type { KhoaDich } from "../../i18n";
import type { LoaiHan } from "./nghiep-vu/nhiem-vu";
import type {
  KieuTruong,
  LoaiDonVi,
  NguonTichHop,
  SuKienWebhook,
  XacThucWebhook,
} from "./types";
import type { LocTrangThai, VaiTroThamGia } from "./types-nhiem-vu";
import type { TrangThaiNhiemVu, UuTienPhanCong } from "./types-phan-cong";
import type { NguonVanBan } from "./types-tom-tat";
/**
 * Bảng nhãn và tông của ba màn Cấu hình xử lý văn bản. Tông đặt ở đây, không ở `mock/` (CLAUDE.md).
 *
 * - Loại đơn vị, nguồn tích hợp là **phân loại** → nhóm `violet`/`teal`/`orange`/`info`.
 * - Trạng thái (đang dùng, đang hoạt động) và điểm đánh giá là **mức độ** → nhóm trạng thái.
 */

export const LOAI_DON_VI: LoaiDonVi[] = ["CO_QUAN", "PHONG_BAN", "CA_NHAN"];

export const NHAN_LOAI_DON_VI: Record<LoaiDonVi, KhoaDich> = {
  CO_QUAN: "vanBanDonVi.loai.CO_QUAN",
  PHONG_BAN: "vanBanDonVi.loai.PHONG_BAN",
  CA_NHAN: "vanBanDonVi.loai.CA_NHAN",
};

export const TONG_LOAI_DON_VI: Record<LoaiDonVi, BadgeVariant> = {
  CO_QUAN: "violet",
  PHONG_BAN: "teal",
  CA_NHAN: "orange",
};

/** Điểm đánh giá 0–10: cao là tốt, như `scoreTone` của bản cũ. */
export function tongDiem(diem: number): BadgeVariant {
  if (diem >= 9) return "success";
  if (diem >= 8) return "primary";
  if (diem >= 6.5) return "warning";
  return "danger";
}

export const NHAN_KIEU_TRUONG: Record<KieuTruong, KhoaDich> = {
  TEXT: "vanBanMau.kieu.TEXT",
  TEXTAREA: "vanBanMau.kieu.TEXTAREA",
  DATE: "vanBanMau.kieu.DATE",
  NUMBER: "vanBanMau.kieu.NUMBER",
  SELECT: "vanBanMau.kieu.SELECT",
};

/** Bố cục thể thức theo Điều 8 NĐ30, hiện ở màn Xem mẫu. */
export const BO_CUC_ND30: KhoaDich[] = [
  "vanBanMau.boCuc.1",
  "vanBanMau.boCuc.2",
  "vanBanMau.boCuc.3",
  "vanBanMau.boCuc.4",
  "vanBanMau.boCuc.5",
  "vanBanMau.boCuc.6",
  "vanBanMau.boCuc.7",
  "vanBanMau.boCuc.8",
  "vanBanMau.boCuc.9",
];

export const NGUON_TICH_HOP: NguonTichHop[] = [
  "QLVB",
  "CONG_DVC",
  "EMAIL",
  "UPLOAD",
  "OTHER",
];

export const NHAN_NGUON: Record<NguonTichHop, KhoaDich> = {
  QLVB: "vanBanTichHop.nguon.QLVB",
  CONG_DVC: "vanBanTichHop.nguon.CONG_DVC",
  EMAIL: "vanBanTichHop.nguon.EMAIL",
  UPLOAD: "vanBanTichHop.nguon.UPLOAD",
  OTHER: "vanBanTichHop.nguon.OTHER",
};

export const TONG_NGUON: Record<NguonTichHop, BadgeVariant> = {
  QLVB: "info",
  CONG_DVC: "violet",
  EMAIL: "teal",
  UPLOAD: "orange",
  OTHER: "neutral",
};

/** Nhãn nguồn là chuỗi tự do (bản cũ không ràng buộc); giá trị lạ hiện nguyên văn. */
export function laNguonBiet(s: string): s is NguonTichHop {
  return (NGUON_TICH_HOP as string[]).includes(s);
}

export const SU_KIEN_WEBHOOK: SuKienWebhook[] = [
  "AFTER_SUMMARIZE",
  "AFTER_COMPOSE",
  "AFTER_OCR",
  "ON_ERROR",
];

export const NHAN_SU_KIEN: Record<SuKienWebhook, KhoaDich> = {
  AFTER_SUMMARIZE: "vanBanTichHop.suKien.AFTER_SUMMARIZE",
  AFTER_COMPOSE: "vanBanTichHop.suKien.AFTER_COMPOSE",
  AFTER_OCR: "vanBanTichHop.suKien.AFTER_OCR",
  ON_ERROR: "vanBanTichHop.suKien.ON_ERROR",
};

export const NHAN_XAC_THUC_WEBHOOK: Record<XacThucWebhook, KhoaDich> = {
  NONE: "vanBanTichHop.xacThuc.NONE",
  API_KEY: "vanBanTichHop.xacThuc.API_KEY",
  BEARER: "vanBanTichHop.xacThuc.BEARER",
};

export { LOAI_VAN_BAN } from "./nghiep-vu/loai-van-ban";

// ─── Tóm tắt văn bản ────────────────────────────────────────────────────────────────────────────

/**
 * Nhãn và tông của màn Tóm tắt văn bản. Nguồn văn bản là **phân loại** → `info`; trạng thái tóm tắt là
 * **mức độ** → `success`/`warning` (như bản cũ: đã tóm tắt xanh, chưa tóm tắt vàng).
 */
export const NHAN_NGUON_VAN_BAN: Record<NguonVanBan, KhoaDich> = {
  QLVB: "vanBanTomTat.nguon.QLVB",
  CONG_DVC: "vanBanTomTat.nguon.CONG_DVC",
  EMAIL: "vanBanTomTat.nguon.EMAIL",
  OTHER: "vanBanTomTat.nguon.OTHER",
};

export const TONG_NGUON_VAN_BAN: BadgeVariant = "info";

export const TONG_DA_TOM_TAT: BadgeVariant = "success";
export const TONG_CHUA_TOM_TAT: BadgeVariant = "warning";

// ─── Phân công nhiệm vụ ─────────────────────────────────────────────────────────────────────────

/**
 * Bảng nhãn và tông của màn Phân công nhiệm vụ. Màu biểu tượng loại đơn vị đi theo `TONG_LOAI_DON_VI`
 * của `constants.ts` (phân loại: violet / teal / orange), để cùng một loại đơn vị mang một màu ở mọi màn.
 */

export const TONG_ICON_LOAI_DON_VI: Record<LoaiDonVi, string> = {
  CO_QUAN: "text-violet-600",
  PHONG_BAN: "text-teal-600",
  CA_NHAN: "text-orange-600",
};

export const TRANG_THAI_NHIEM_VU: TrangThaiNhiemVu[] = [
  "MOI_PHAN_CONG",
  "DANG_THUC_HIEN",
  "HOAN_THANH",
];

export const NHAN_TRANG_THAI_NHIEM_VU: Record<TrangThaiNhiemVu, KhoaDich> = {
  MOI_PHAN_CONG: "vanBanPhanCong.trangThai.MOI_PHAN_CONG",
  DANG_THUC_HIEN: "vanBanPhanCong.trangThai.DANG_THUC_HIEN",
  HOAN_THANH: "vanBanPhanCong.trangThai.HOAN_THANH",
};

export const UU_TIEN_PHAN_CONG: UuTienPhanCong[] = [
  "AUTO",
  "PHONG_BAN",
  "CA_NHAN",
];

export const NHAN_UU_TIEN: Record<UuTienPhanCong, KhoaDich> = {
  AUTO: "vanBanPhanCong.uuTien.AUTO",
  PHONG_BAN: "vanBanPhanCong.uuTien.PHONG_BAN",
  CA_NHAN: "vanBanPhanCong.uuTien.CA_NHAN",
};

/** Hạn xử lý mặc định cho lượt phân công từ tệp (ngày), như bản cũ. */
export const HAN_MAC_DINH = [7, 14, 30] as const;

/** Tệp nhận cho "Upload file & AI phân công": khớp `TrichXuat` của backend (pdf/docx/txt/md, ADR-0084). */
export const DUOI_TEP_PHAN_CONG = ["pdf", "docx", "txt", "md"] as const;
export const TOI_DA_MB = 20;

// ─── Quản lý nhiệm vụ ───────────────────────────────────────────────────────────────────────────

/**
 * Bảng tông của màn Quản lý nhiệm vụ. Trạng thái dùng nhóm biến thể **trạng thái** của `Badge`
 * (chưa bắt đầu `neutral`, đang làm `primary`, sắp đến hạn `warning`, quá hạn `danger`, xong
 * `success`); vai trò người tham gia là **phân loại** nên dùng nhóm phân loại.
 *
 * Chấm và thanh dùng bậc `-500`, chữ bậc `-700` (WCAG AA), như `Badge`.
 */
interface TongTrangThai {
  badge: BadgeVariant;
  /** Chấm, đoạn thanh tỷ lệ. */
  dot: string;
  /** Ô biểu tượng của thẻ KPI. */
  chip: string;
  /** Số nổi bật khi đáng báo động. */
  value: string;
}

export const TONG_TRANG_THAI: Record<LocTrangThai, TongTrangThai> = {
  MOI_PHAN_CONG: {
    badge: "neutral",
    dot: "bg-dark-400",
    chip: "bg-subtle text-content-secondary",
    value: "text-content-primary",
  },
  DANG_THUC_HIEN: {
    badge: "primary",
    dot: "bg-primary-500",
    chip: "bg-primary-50 text-primary-600",
    value: "text-primary-700",
  },
  SAP_DEN_HAN: {
    badge: "warning",
    dot: "bg-warning-500",
    chip: "bg-warning-50 text-warning-600",
    value: "text-warning-700",
  },
  QUA_HAN: {
    badge: "danger",
    dot: "bg-danger-500",
    chip: "bg-danger-50 text-danger-600",
    value: "text-danger-700",
  },
  HOAN_THANH: {
    badge: "success",
    dot: "bg-success-500",
    chip: "bg-success-50 text-success-600",
    value: "text-success-700",
  },
};

/** Lớp chữ/nền của nhãn trạng thái dạng nút (không dùng được `Badge` vì phải bấm được). */
export const LOP_NHAN_TRANG_THAI: Record<LocTrangThai, string> = {
  MOI_PHAN_CONG: "bg-subtle text-content-secondary",
  DANG_THUC_HIEN: "bg-primary-50 text-primary-700",
  SAP_DEN_HAN: "bg-warning-50 text-warning-700",
  QUA_HAN: "bg-danger-50 text-danger-700",
  HOAN_THANH: "bg-success-50 text-success-700",
};

export const TONG_VAI_TRO: Record<VaiTroThamGia, BadgeVariant> = {
  CHU_TRI: "violet",
  PHOI_HOP: "teal",
  XEM_DE_BIET: "info",
};

/** Avatar chữ cái đầu: chỉ tông phân loại và màu nhấn, không tông trạng thái. */
export const TONG_AVATAR = [
  "bg-orange-600",
  "bg-violet-600",
  "bg-teal-600",
  "bg-sky-600",
  "bg-primary-600",
  "bg-dark-600",
];

export const TONG_HAN: Record<LoaiHan, string> = {
  overdue: "bg-danger-50 text-danger-700",
  today: "bg-danger-50 text-danger-700",
  soon: "bg-warning-50 text-warning-700",
  later: "text-content-secondary",
  done: "text-content-muted",
  none: "text-content-muted",
};

export const NHAN_TRANG_THAI: Record<LocTrangThai, KhoaDich> = {
  MOI_PHAN_CONG: "vanBanNhiemVu.trangThai.MOI_PHAN_CONG",
  DANG_THUC_HIEN: "vanBanNhiemVu.trangThai.DANG_THUC_HIEN",
  HOAN_THANH: "vanBanNhiemVu.trangThai.HOAN_THANH",
  QUA_HAN: "vanBanNhiemVu.trangThai.QUA_HAN",
  SAP_DEN_HAN: "vanBanNhiemVu.trangThai.SAP_DEN_HAN",
};

export const NHAN_VAI_TRO: Record<VaiTroThamGia, KhoaDich> = {
  CHU_TRI: "vanBanNhiemVu.vaiTro.CHU_TRI",
  PHOI_HOP: "vanBanNhiemVu.vaiTro.PHOI_HOP",
  XEM_DE_BIET: "vanBanNhiemVu.vaiTro.XEM_DE_BIET",
};

export const NHAN_HAN: Record<LoaiHan, KhoaDich> = {
  none: "vanBanNhiemVu.han.none",
  overdue: "vanBanNhiemVu.han.overdue",
  today: "vanBanNhiemVu.han.today",
  soon: "vanBanNhiemVu.han.soon",
  later: "vanBanNhiemVu.han.later",
  done: "vanBanNhiemVu.han.done",
};

export const NHAN_PHAM_VI = {
  mine: "vanBanNhiemVu.phamVi.mine",
  assigned: "vanBanNhiemVu.phamVi.assigned",
  all: "vanBanNhiemVu.phamVi.all",
} as const satisfies Record<string, KhoaDich>;

/** Trường đổi ở dòng thời gian `UPDATED` (`fields` của backend). */
export const NHAN_TRUONG: Record<string, KhoaDich> = {
  title: "vanBanNhiemVu.truongDoi.title",
  description: "vanBanNhiemVu.truongDoi.description",
  deadline: "vanBanNhiemVu.truongDoi.deadline",
  related_document: "vanBanNhiemVu.truongDoi.related_document",
  lead_org: "vanBanNhiemVu.truongDoi.lead_org",
  attachment: "vanBanNhiemVu.truongDoi.attachment",
};
